import { type Nullable } from '../../types/common'
import { hideElement, showElement } from '../../utils/dom'
import BaseComponent from '../base-component/base-component'
import { TimerStorage } from './timer-storage'
import {
  type TimeUnit,
  TimerMode as Mode,
  TimerFinishStrategy as FinishStrategy,
  type TimerSettingsHash
} from './types'

const ONE_DAY_MS = 86400000

const MONTHS = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11
}

const getEvergreenEndDate = (unit: TimeUnit, value: number): Date => {
  const date = new Date()
  switch (unit) {
    case 'day':
      date.setDate(date.getDate() + value)
      break
    case 'hour':
      date.setHours(date.getHours() + value)
      break
    case 'minute':
      date.setMinutes(date.getMinutes() + value)
      break
    case 'second':
      date.setSeconds(date.getSeconds() + value)
      break
  }
  return date
}

class CountdownTimer extends BaseComponent {
  mode = Mode.Fixed
  endDate = new Date()
  timerRef: number
  finishStrategy = FinishStrategy.None
  timerStorage: TimerStorage
  evergreenUnit: TimeUnit
  evergreenValue: number

  daysUnit: Nullable<HTMLElement>
  hoursUnit: Nullable<HTMLElement>
  minutesUnit: Nullable<HTMLElement>
  secondsUnit: Nullable<HTMLElement>
  daysUnitLabel: Nullable<HTMLElement>
  hoursUnitLabel: Nullable<HTMLElement>
  minutesUnitLabel: Nullable<HTMLElement>
  secondsUnitLabel: Nullable<HTMLElement>

  constructor () {
    super()

    this.daysUnit = this.querySelector('[data-timer-days]')
    this.hoursUnit = this.querySelector('[data-timer-hours]')
    this.minutesUnit = this.querySelector('[data-timer-minutes]')
    this.secondsUnit = this.querySelector('[data-timer-seconds]')
    this.daysUnitLabel = this.querySelector('[data-timer-days-label]')
    this.hoursUnitLabel = this.querySelector('[data-timer-hours-label]')
    this.minutesUnitLabel = this.querySelector('[data-timer-minutes-label]')
    this.secondsUnitLabel = this.querySelector('[data-timer-seconds-label]')

    this.evergreenUnit = (this.dataset.evergreenUnit as TimeUnit) || 'minute'
    this.evergreenValue = parseInt(this.dataset.evergreenValue || '0')

    this.mode = this.dataset.mode as Mode
    if (this.mode === Mode.Fixed) {
      this.finishStrategy =
        this.dataset.hideOnComplete === 'true'
          ? FinishStrategy.Hide
          : FinishStrategy.None
    }

    if (this.mode === Mode.Evergreen) {
      this.finishStrategy = this.dataset.evergreenOnComplete as FinishStrategy || FinishStrategy.Restart
      this.timerStorage = new TimerStorage(this.dataset.blockId || this.id)
    }
  }

  connectedCallback (): void {
    this.start()
  }

  disconnectedCallback (): void {
    clearInterval(this.timerRef)
  }

  getHash (): TimerSettingsHash {
    return `${this.finishStrategy}-${this.evergreenUnit}-${this.evergreenValue}`
  }

  start (): void {
    if (this.mode === Mode.Fixed) {
      const year = Number(this.dataset.year)
      const month = MONTHS[this.dataset.month ?? 'january']
      const day = Number(this.dataset.day)
      const hour = Number(this.dataset.hour)
      const minute = Number(this.dataset.minute)

      this.endDate = new Date(year, month, day, hour, minute)
    }

    if (this.mode === Mode.Evergreen) {
      const timerData = this.timerStorage.loadData()
      if (!timerData || timerData.hash !== this.getHash()) {
        this.updateEvergreenData()
      } else {
        this.endDate = timerData.endDate
      }
    }

    this.timerRef = window.setInterval(this.tick.bind(this), 1000)
    this.tick()
  }

  updateEvergreenData (): void {
    const endDate = getEvergreenEndDate(this.evergreenUnit, this.evergreenValue)
    this.timerStorage.saveData({ endDate, hash: this.getHash() })
    this.endDate = endDate
  }

  finish (): void {
    clearInterval(this.timerRef)
    switch (this.finishStrategy) {
      case FinishStrategy.Restart:
        this.timerStorage.saveData({
          endDate: getEvergreenEndDate(this.evergreenUnit, this.evergreenValue),
          hash: this.getHash()
        })
        this.start()
        break
      case FinishStrategy.WaitOneDay:
        if (new Date().getTime() - this.endDate.getTime() > ONE_DAY_MS) {
          const endDate = getEvergreenEndDate(
            this.evergreenUnit,
            this.evergreenValue
          )
          this.timerStorage.saveData({ endDate, hash: this.getHash() })
          this.start()
        } else {
          this.hide()
        }
        break
      case FinishStrategy.None:
        this.show() // show zero-values if time is over
        break
      default:
        this.hide()
    }
  }

  renderTime (
    days: number,
    hours: number,
    minutes: number,
    seconds: number
  ): void {
    if (
      !this.daysUnit ||
      !this.hoursUnit ||
      !this.minutesUnit ||
      !this.secondsUnit
    ) {
      return
    }
    this.daysUnit.innerText = days.toString()
    this.hoursUnit.innerText = hours.toString()
    this.minutesUnit.innerText = minutes.toString()
    this.secondsUnit.innerText = seconds.toString()

    this.renderLabels(days, hours, minutes, seconds)
  }

  renderLabels (
    days: number,
    hours: number,
    minutes: number,
    seconds: number
  ): void {
    if (
      !this.daysUnitLabel ||
      !this.hoursUnitLabel ||
      !this.minutesUnitLabel ||
      !this.secondsUnitLabel
    ) {
      return
    }
    this.daysUnitLabel.innerText =
      days === 1
        ? window.auroraThemeLocales.countdownTimerString.day
        : window.auroraThemeLocales.countdownTimerString.days
    this.hoursUnitLabel.innerText =
      hours === 1
        ? window.auroraThemeLocales.countdownTimerString.hour
        : window.auroraThemeLocales.countdownTimerString.hours
    this.minutesUnitLabel.innerText =
      minutes === 1
        ? window.auroraThemeLocales.countdownTimerString.minute
        : window.auroraThemeLocales.countdownTimerString.minutes
    this.secondsUnitLabel.innerText =
      seconds === 1
        ? window.auroraThemeLocales.countdownTimerString.second
        : window.auroraThemeLocales.countdownTimerString.seconds
  }

  tick (): void {
    const diff = this.endDate.getTime() - new Date().getTime()

    if (diff > 0) {
      this.show()
      this.renderTime(
        Math.floor(diff / (1000 * 60 * 60 * 24)),
        Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        Math.floor((diff % (1000 * 60)) / 1000)
      )
      return
    }
    this.renderTime(0, 0, 0, 0)
    this.finish()

    if (window.Shopify.designMode) {
      this.show()
    }
  }

  show (): void {
    showElement(this)
    if (this.dataset.blockId) {
      showElement(document.querySelector(`.block-${this.dataset.blockId}`))
    }
  }

  hide (): void {
    if (window.Shopify.designMode) {
      return
    }

    if (this.dataset.sectionId) {
      hideElement(document.querySelector(`.section-${this.dataset.sectionId}`))
    }

    if (this.dataset.blockId) {
      hideElement(document.querySelector(`.block-${this.dataset.blockId}`))
    }

    hideElement(this)
  }
}

export default CountdownTimer
